import { Injectable, ElementRef, ApplicationRef, EnvironmentInjector, createComponent, ComponentRef, inject } from '@angular/core';
import * as L from 'leaflet';
import { FullScreen } from 'leaflet.fullscreen';
import 'leaflet-easybutton';
import { MeetingRoomCoordinate } from '../data/room-coordinates.data';
import { Device, getDeviceIcon } from './device.service';
import { DevicePopupComponent } from '../components/device-popup/device-popup.component';

@Injectable({
    providedIn: 'root'
})
export class MapService {
    private appRef = inject(ApplicationRef);
    private injector = inject(EnvironmentInjector);
    private popupRefs = new Map<string, ComponentRef<DevicePopupComponent>>();

    private map: L.Map | undefined;
    private markers = new Map<string, L.Marker>();
    private markerStatuses = new Map<string, string>(); // deviceId -> 'STATUS:angle' 用於避免不必要的 setIcon
    private pendingDeviceId: string | null = null;
    private imageOverlay: L.ImageOverlay | undefined;
    private roomRectangles = new Map<string, L.Polygon>();

    private imageUrl = 'everbrite_14f.svg';
    private bounds: L.LatLngBoundsExpression = [[0, 0], [1000, 1500]];

    private floorImages: Record<number, string> = {
        14: 'everbrite_14f.svg',
        15: 'everbrite_15f.svg'
    };

    initMap(mapContainer: ElementRef, floorDevices: Device[], roomCoordinates: any, currentFloor: number, draggable: boolean = false) {
        if (this.map) return;

        this.map = L.map(mapContainer.nativeElement, {
            crs: L.CRS.Simple,
            minZoom: -0.7,
            maxZoom: 2,
            zoomDelta: 0.1,
            zoomSnap: 0.1,
            zoomControl: false,
            attributionControl: false
        });

        this.imageUrl = this.floorImages[currentFloor] || 'everbrite_14f.svg';
        this.imageOverlay = L.imageOverlay(this.imageUrl, this.bounds).addTo(this.map);

        this.map.fitBounds(this.bounds);
        this.map.setZoom(-0.7);

        this.map.addControl(new FullScreen({ position: 'topright' }));
        L.control.zoom({ position: 'topright' }).addTo(this.map);
        const easyButtonFn = (L as any).easyButton || (window as any).L?.easyButton;
        if (easyButtonFn) {
            easyButtonFn({
                states: [{
                    stateName: 'reset-bounds',
                    icon: '<i class="pi pi-refresh" style="line-height: inherit; display: flex; align-items: center; justify-content: center; width: 100%; height: 100%;"></i>',
                    title: '重設大小與位置',
                    onClick: (btn: any, map: any) => {
                        map.setView([500, 750], -0.7);
                    }
                }],
                position: 'topright'
            }).addTo(this.map);
        }

        this.map.invalidateSize();

        this.updateMarkers(floorDevices, draggable);
        this.updateMeetingRoomOverlays(roomCoordinates, currentFloor, floorDevices);
    }

    updateMapImage(floor: number) {
        if (!this.map) return;
        const newImage = this.floorImages[floor];
        if (!newImage || newImage === this.imageUrl) return; // 底圖未變則跳過

        if (this.imageOverlay) {
            this.imageOverlay.remove();
        }
        this.imageUrl = newImage;
        this.imageOverlay = L.imageOverlay(this.imageUrl, this.bounds).addTo(this.map);
    }

    updateMeetingRoomOverlays(roomCoordinates: any, currentFloor: number, devices: Device[]) {
        if (!this.map) return;

        const floorKey = currentFloor.toString();
        const rooms = roomCoordinates[floorKey] || [];

        // Remove rooms that are no longer in the current floor data
        const currentRoomIds = new Set(rooms.map((r: MeetingRoomCoordinate) => r.id));
        this.roomRectangles.forEach((poly, id) => {
            if (!currentRoomIds.has(id)) {
                poly.remove();
                this.roomRectangles.delete(id);
            }
        });

        rooms.forEach((room: MeetingRoomCoordinate) => {
            let status = room.status;

            if (room.code) {
                const device = devices.find(d => d.code === room.code);
                if (device && device.data?.['01_1']) {
                    const val = Number(device.data['01_1'].value);
                    switch (val) {
                        case 0: status = 'AVAILABLE'; break;
                        case 1: status = 'RESERVED'; break;
                        case 2: status = 'OCCUPIED'; break;
                    }
                }
            }

            let color = '#9ca3af';
            switch (status) {
                case 'AVAILABLE':
                    color = '#dddddd';
                    break;
                case 'RESERVED':
                    color = '#eab308';
                    break;
                case 'OCCUPIED':
                    color = '#22c55e';
                    break;
            }

            const existingPoly = this.roomRectangles.get(room.id);
            if (existingPoly) {
                // Update existing polygon incrementally
                existingPoly.setStyle({
                    color: color,
                    fillColor: color
                });
            } else {
                // Create new polygon
                const poly = L.polygon(room.polygon, {
                    color: color,
                    weight: 1,
                    fillColor: color,
                    fillOpacity: 0.3
                }).addTo(this.map!);

                this.roomRectangles.set(room.id, poly);
            }
        });
    }

    updateMarkers(devices: Device[], draggable: boolean = true) {
        if (!this.map) return;

        // Remove markers that are no longer in the new devices list
        const currentDeviceIds = new Set(devices.map(d => d.id));
        this.markers.forEach((marker, id) => {
            if (!currentDeviceIds.has(id)) {
                marker.remove();
                this.markers.delete(id);
                const popupRef = this.popupRefs.get(id);
                if (popupRef) {
                    this.appRef.detachView(popupRef.hostView);
                    popupRef.destroy();
                    this.popupRefs.delete(id);
                }
            }
        });

        devices.forEach(d => {
            let colorClass = 'bg-[#9F9F9F]';

            if (d.status === 'NORMAL') {
                colorClass = 'bg-[#3FBAB9]';
            } else if (d.status === 'ALARM') {
                colorClass = 'bg-[#FB2C36] animate-pulse';
            } else if (d.status === 'MAINTENANCE') {
                colorClass = 'bg-[#9F9F9F]';
            }

            const iconHtml = `
            <div class="relative w-8 h-8 flex items-center justify-center">
                 ${d.status === 'ALARM' ? '<div class="absolute w-full h-full rounded-full bg-red-500 animate-ping opacity-60"></div>' : ''}
                 <div class="relative w-full h-full rounded-full ${colorClass} border-2 border-white shadow-lg flex items-center justify-center text-white transition-all hover:scale-110">
                     <div class="w-full h-full flex items-center justify-center" style="transform: rotate(${d.angle || 0}deg)">
                         <i class="ci ${getDeviceIcon(d.type)}"></i>
                     </div>
                 </div>
            </div>
          `;

            const icon = L.divIcon({
                html: iconHtml,
                className: 'custom-div-icon',
                iconSize: [32, 32],
                iconAnchor: [16, 16]
            });

            const existingMarker = this.markers.get(d.id);
            const popupRef = this.popupRefs.get(d.id);

            if (existingMarker && popupRef) {
                // ✅ 只有 status 或角度真的改變時才重建 icon DOM，避免打斷 animate-ping 並防止 Popup 失效
                const statusKey = `${d.status}:${d.angle ?? 0}`;
                if (this.markerStatuses.get(d.id) !== statusKey) {
                    existingMarker.setIcon(icon);
                    this.markerStatuses.set(d.id, statusKey);
                }

                // Update the Angular component's input to trigger UI reactivity
                popupRef.setInput('device', d);
                popupRef.changeDetectorRef.detectChanges();

                // Update draggable state if it changed
                if (draggable) {
                    existingMarker.dragging?.enable();
                } else {
                    existingMarker.dragging?.disable();
                }
            } else {
                // Create new marker
                const marker = L.marker(d.position as L.LatLngExpression, {
                    icon,
                    draggable: draggable
                }).addTo(this.map!);

                const popupComponentRef = createComponent(DevicePopupComponent, {
                    environmentInjector: this.injector
                });
                popupComponentRef.setInput('device', d);
                popupComponentRef.instance.close.subscribe(() => {
                    marker.closePopup();
                });
                popupComponentRef.changeDetectorRef.detectChanges();
                this.appRef.attachView(popupComponentRef.hostView);
                const domElement = (popupComponentRef.hostView as any).rootNodes[0] as HTMLElement;

                // 阻止事件傳播到地圖上，避免滾動彈窗時地圖縮放、點擊彈窗時觸發地圖點擊
                L.DomEvent.disableScrollPropagation(domElement);
                L.DomEvent.disableClickPropagation(domElement);

                marker.bindPopup(domElement, {
                    className: 'custom-dark-popup',
                    minWidth: 200,
                    closeButton: false,
                    autoClose: false,
                    closeOnClick: false,
                    offset: [0, -10]
                });

                marker.on('popupopen', () => {
                    marker.getElement()?.classList.add('marker-active');
                });

                marker.on('popupclose', () => {
                    marker.getElement()?.classList.remove('marker-active');
                });

                this.popupRefs.set(d.id, popupComponentRef);

                marker.on('dragend', (e) => {
                    const pos = e.target.getLatLng();
                    d.position = [Math.round(pos.lat), Math.round(pos.lng)];
                    console.log(`[點位移動] ${d.name} 移動至 [${d.position[0]}, ${d.position[1]}]`);
                });

                this.markers.set(d.id, marker);
                this.markerStatuses.set(d.id, `${d.status}:${d.angle ?? 0}`);
            }
        });
    }

    updateDraggable(draggable: boolean) {
        this.markers.forEach(m => {
            if (draggable) {
                m.dragging?.enable();
            } else {
                m.dragging?.disable();
            }
        });
    }

    setPendingHighlight(deviceId: string) {
        this.pendingDeviceId = deviceId;
    }

    processPendingHighlight() {
        if (this.pendingDeviceId && this.markers.has(this.pendingDeviceId)) {
            this.highlightMarker(this.pendingDeviceId);
            this.pendingDeviceId = null;
        }
    }

    highlightMarker(id: string) {
        const marker = this.markers.get(id);
        if (marker && this.map) {
            marker.openPopup();
            this.map.panTo(marker.getLatLng());
        }
    }

    destroyMap() {
        if (this.map) {
            this.map.remove();
            this.map = undefined;
        }
        this.markers.clear();
        this.markerStatuses.clear();
        this.roomRectangles.clear();
        this.popupRefs.forEach(p => {
            this.appRef.detachView(p.hostView);
            p.destroy();
        });
        this.popupRefs.clear();
        this.imageOverlay = undefined;
    }


}
